import { useEffect, useState, type CSSProperties } from 'react';
import { theme } from '../theme';
import { api, type Providers } from '../api';

const loginBtn: CSSProperties = {
  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 14px', background: 'transparent', color: theme.text,
  border: `1px solid ${theme.line}`, borderRadius: 10,
  fontFamily: 'inherit', fontSize: 13, cursor: 'pointer', marginBottom: 8,
  transition: 'background 120ms, border-color 120ms',
};

export function Login({ next }: { next: string | null }) {
  const [providers, setProviders] = useState<Providers | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    api.providers().then(setProviders).catch(() => setProviders({ google: false, github: false }));
  }, []);

  const startOAuth = (provider: 'google' | 'github') => {
    setPending(provider);
    const path = `/api/auth/${provider}` + (next ? `?next=${encodeURIComponent(next)}` : '');
    window.location.href = path;
  };

  const startDev = async () => {
    setPending('dev');
    try {
      await api.devLogin();
      window.location.href = next ?? '/';
    } catch {
      setPending(null);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'grid', placeItems: 'center',
      padding: 24, background: theme.bg, color: theme.text,
    }}>
      <div style={{
        width: 400, padding: '36px 32px',
        background: theme.surface, border: `1px solid ${theme.line}`,
        borderRadius: 16,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: `${theme.accent}22`, color: theme.accent,
          display: 'grid', placeItems: 'center', fontWeight: 700,
          fontSize: 15, marginBottom: 22,
        }}>S</div>
        <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 6 }}>
          Welcome back
        </div>
        <div style={{ fontSize: 13, color: theme.dim, marginBottom: 26 }}>
          {next ? 'Sign in to continue.' : 'Sign in to see your apps.'}
        </div>

        {providers?.google && (
          <button
            onClick={() => startOAuth('google')}
            disabled={pending !== null}
            style={loginBtn}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = theme.line2; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = theme.line; }}
          >
            <span style={{ width: 16, color: theme.accent, textAlign: 'center' }}>◇</span>
            Continue with Google
          </button>
        )}

        {providers?.github && (
          <button
            onClick={() => startOAuth('github')}
            disabled={pending !== null}
            style={loginBtn}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = theme.line2; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = theme.line; }}
          >
            <span style={{ width: 16, color: theme.accent, textAlign: 'center' }}>◆</span>
            Continue with GitHub
          </button>
        )}

        {providers?.dev && (
          <>
            <div style={{
              margin: '20px 0 14px', display: 'flex', alignItems: 'center', gap: 10,
              color: theme.veryDim, fontSize: 11,
            }}>
              <div style={{ flex: 1, height: 1, background: theme.line }} />
              local only
              <div style={{ flex: 1, height: 1, background: theme.line }} />
            </div>
            <button
              onClick={startDev}
              disabled={pending !== null}
              style={{
                ...loginBtn,
                borderColor: theme.err,
                color: theme.err,
              }}
            >
              <span style={{ width: 16, textAlign: 'center' }}>!</span>
              Dev login
              <span style={{ marginLeft: 'auto', fontSize: 10, color: theme.veryDim }}>
                non-prod
              </span>
            </button>
          </>
        )}

        {providers && !providers.google && !providers.github && !providers.dev && (
          <div style={{
            border: `1px solid ${theme.err}`, borderRadius: 10,
            padding: 12, color: theme.err, fontSize: 12,
          }}>
            No sign-in method configured. Set <code>GOOGLE_CLIENT_ID</code>,{' '}
            <code>GITHUB_CLIENT_ID</code>, or <code>DEV_LOGIN_EMAIL</code> and restart.
          </div>
        )}

        {pending && (
          <div style={{
            marginTop: 14, fontSize: 12, color: theme.dim, textAlign: 'center',
          }}>
            {pending === 'dev' ? 'signing in…' : `redirecting to ${pending}…`}
          </div>
        )}
      </div>
    </div>
  );
}
